import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useUploadGuard } from '@/components/UploadGuard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BulkUpload({ user, onLogout }) {
  const navigate = useNavigate();
  const [file, setFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [template, setTemplate] = React.useState(null);
  const [dragActive, setDragActive] = React.useState(false);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);

  React.useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/bulk-upload/template`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Failed to fetch template:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selectedFile);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); return; }

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_company_id', targetCo);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/bookings/bulk-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} bookings!`);
      }
      
      if (response.data.skipped > 0) {
        toast.warning(`Skipped ${response.data.skipped} rows`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Upload failed: ' + errorMsg);
      setResult({ error: errorMsg });
    }

    setUploading(false);
  };

  const downloadSampleTemplate = () => {
    // Create a sample CSV content
    const headers = template?.headers || [];
    const csvContent = headers.join(',') + '\n' + 
      'Mr,John,Doe,Smith,1990-01-15,Male,9876543210,9876543211,9876543210,john@example.com,Building A,101,123 Main St,,Downtown,Mumbai,Maharashtra,400001,123456789012,ABCDE1234F,22AAAAA0000A1Z5,4 wheeler,Personal,Toyota,Camry,XLE,White,Petrol,CHAS123456,ENG123456,ICICI Lombard,POL123456,Mumbai Central,MH01AB1234,2024-01-15';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template downloaded! Open in Excel and save as .xlsx');
  };

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Bulk Upload">
      {guardModal}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white p-6 rounded-lg border mb-6">
          <h2 className="text-2xl font-bold mb-2">Bulk Upload Bookings</h2>
          <p className="text-gray-600 mb-6">
            Import multiple bookings from an Excel file. Use the same format as Google Sheets export.
          </p>

          {/* Upload Area */}
          <div
            data-testid="drop-zone"
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : file 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <FileSpreadsheet className="w-16 h-16 mx-auto text-green-600" />
                <div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                  }}
                  className="text-sm text-red-600 hover:underline"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-16 h-16 mx-auto text-gray-400" />
                <div>
                  <p className="font-medium text-lg">Drag & drop your Excel file here</p>
                  <p className="text-sm text-gray-500">or click to browse</p>
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block px-6 py-2 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700"
                  data-testid="select-file-btn"
                >
                  Select File
                </label>
              </div>
            )}
          </div>

          {/* Upload Button */}
          {file && (
            <div className="mt-6 flex gap-4">
              <button
                onClick={handleUpload}
                disabled={uploading}
                data-testid="upload-btn"
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload & Import
                  </>
                )}
              </button>
            </div>
          )}

          {/* Results */}
          {result && (
            <div data-testid="upload-result" className={`mt-6 p-4 rounded-lg ${result.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              {result.error ? (
                <div className="flex items-start gap-3">
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800" data-testid="upload-error">Upload Failed</p>
                    <p className="text-sm text-red-600 mt-1">{result.error}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <p className="font-medium text-green-800" data-testid="upload-success">Upload Completed!</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-white p-3 rounded border">
                      <p className="text-2xl font-bold text-green-600" data-testid="imported-count">{result.imported}</p>
                      <p className="text-sm text-gray-600">Records Imported</p>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      <p className="text-2xl font-bold text-yellow-600" data-testid="skipped-count">{result.skipped}</p>
                      <p className="text-sm text-gray-600">Rows Skipped</p>
                    </div>
                  </div>

                  {result.unmapped_columns?.length > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-800">Unmapped Columns</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            These columns were ignored: {result.unmapped_columns.join(', ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {result.errors?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-sm text-red-600 space-y-1">
                        {result.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => navigate('/sales/bookings')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    View Bookings
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Template Info */}
        <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Excel File Format
          </h3>
          
          <div className="space-y-4">
            <div>
              <p className="font-medium mb-2">Required Columns:</p>
              <div className="flex flex-wrap gap-2">
                {template?.required_fields?.map((field, idx) => (
                  <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 text-sm rounded">
                    {field} *
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="font-medium mb-2">Supported Columns (same as Google Sheets):</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {template?.headers?.slice(0, 20).map((header, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white text-gray-700 text-sm rounded border">
                    {header}
                  </span>
                ))}
                {template?.headers?.length > 20 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-500 text-sm rounded">
                    +{template.headers.length - 20} more...
                  </span>
                )}
              </div>
            </div>

            <div className="p-3 bg-white border border-blue-200 rounded">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> {template?.note || 'Booking No is optional. If not provided or duplicate, a new number will be generated.'}
              </p>
            </div>

            <button
              onClick={downloadSampleTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
            >
              <Download className="w-4 h-4" />
              Download Sample Template (CSV)
            </button>
          </div>
        </div>
      </div>
    </SalesLayout>
  );
}

export default BulkUpload;
